/**
 * KAN-529 — "the server says this account must finish 2FA" signal.
 *
 * The server now refuses a 2FA-required user who never enrolled (DB predicate
 * `current_user_satisfies_mfa()` + edge `requireAal2IfEnrolled`). The app must
 * answer that refusal by showing the forced-enrollment screen, never a raw
 * error. ProtectedRoute already asks the server up front
 * (`get_my_mfa_requirement`), so this signal is the BACKSTOP for a refusal that
 * arrives mid-session — e.g. an admin adds the user's role to the company's
 * required list, or a session's grandfather window ends.
 *
 * Wiring: `src/supabase.js` hands every non-OK `/rest/v1/` + `/functions/v1/`
 * response (a CLONE — the caller keeps the original) to
 * `checkResponseForMfaRequired`; a match calls `emitMfaRequired()`, and
 * ProtectedRoute re-runs its 2FA check on emit.
 *
 * Recognized refusals (all stable server contracts):
 *   - edge  : `{ code: 'MFA_REQUIRED' }` or `{ error: { code: 'MFA_REQUIRED' } }`
 *             (_shared/requireAal2.ts; 403)
 *   - DB RPC: PostgREST error whose `message` is `MFA_REQUIRED` (or starts with
 *             `MFA_REQUIRED:`) — every DEFINER 2FA gate raises that text.
 * A plain RLS denial is NOT matched (it is ambiguous); the up-front check
 * covers those.
 */

const listeners = new Set();

/** @param {unknown} body parsed JSON error body */
export function isMfaRequiredErrorBody(body) {
  if (!body || typeof body !== 'object') return false;
  if (body.code === 'MFA_REQUIRED') return true;
  if (body.error && typeof body.error === 'object' && body.error.code === 'MFA_REQUIRED') return true;
  const message = typeof body.message === 'string' ? body.message : '';
  return message === 'MFA_REQUIRED' || message.startsWith('MFA_REQUIRED:');
}

export function subscribeMfaRequired(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitMfaRequired() {
  listeners.forEach((listener) => {
    try { listener(); } catch { /* a listener bug must never break the fetch path */ }
  });
}

/**
 * Fire-and-forget inspection of a CLONED non-OK response. Never throws.
 * @param {Response} response
 */
export async function checkResponseForMfaRequired(response) {
  try {
    if (!response || ![400, 401, 403].includes(response.status)) return false;
    const type = response.headers?.get?.('content-type') || '';
    if (!type.includes('json')) return false;
    const body = await response.json();
    if (!isMfaRequiredErrorBody(body)) return false;
    emitMfaRequired();
    return true;
  } catch {
    return false;
  }
}

export function __resetMfaRequiredListenersForTests() {
  listeners.clear();
}
